import { ContractType, Object as VaeObject } from '@vae/server/dist/client'
import { FixedProductDetails } from '@vae/server/src/types/synerallApiTypes'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { backendCall } from '../../modules/backendApi'
import { trpc } from '../../modules/trpc'

export type PriceHook = ReturnType<typeof useContractPrices>

export const useContractPrices = (object: VaeObject) => {
	const [contractType, setContractType] = useState<ContractType>('spot')
	const [spotMargin, setSpotMargin] = useState<number | null>(null)
	const [fixedPrice, setFixedPrice] = useState<number | null>(null)
	const [monthlyPrice, setMonthlyPrice] = useState<number | null>(null)
	const [endDate, setEndDate] = useState<null | string>(null)
	const [loading, setLoading] = useState(true)

	const [weeklySpotAverage, setWeeklySpotAverage] = useState(0)
	const [last30DaysSpotAverage, setLast30DaysSpotAverage] = useState(0)
	const [thisMonthSpotAverage, setThisMonthSpotAverage] = useState(0)

	const [isLockInContract, setIsLockInContract] = useState(false)
	const [currentLockIn, setCurrentLockIn] = useState<FixedProductDetails | null>(null)
	const [upcomingLockIns, setUpcomingLockIns] = useState<FixedProductDetails[]>([])
	const [oldLockIns, setOldLockIns] = useState<FixedProductDetails[]>([])

	const { data: priceLockContracts, isLoading: priceLockContractsLoading } =
		trpc.synerall.contractPriceLocks.useQuery(
			{
				contractId: object.synerallContractId ?? 0,
			},
			{ enabled: !!object?.synerallContractId }
		)

	useEffect(() => {
		if (priceLockContractsLoading) return

		if (!priceLockContracts || priceLockContracts.length === 0) {
			setIsLockInContract(false)
			return
		}

		setIsLockInContract(true)

		const currentLockIn = priceLockContracts.find(lockIn =>
			dayjs().isBetween(dayjs(lockIn.validFrom), dayjs(lockIn.validTo))
		)

		setCurrentLockIn(currentLockIn ?? null)

		setUpcomingLockIns(
			priceLockContracts.filter(lockIn => dayjs().isBefore(dayjs(lockIn.validFrom)))
		)

		setOldLockIns(priceLockContracts.filter(lockIn => dayjs().isAfter(dayjs(lockIn.validTo))))
	}, [priceLockContracts, priceLockContractsLoading])

	useEffect(() => {
		if (!object.id) return

		initialize()
	}, [object])

	const initialize = async () => {
		if (object.synerallContractId) {
			await handleSynePrices(object.synerallContractId)
		} else {
			handleManualValues()
		}

		await Promise.all([getWeeklySpotAverage(), getMonthlySpotAverage()])

		setLoading(false)
	}

	const handleManualValues = () => {
		setContractType(object.contractType ?? 'spot')
		setSpotMargin(object.spotMarginal)
		setFixedPrice(object.fixedPrice)
		setMonthlyPrice(object.monthlyPrice)
	}

	const handleSynePrices = async (contractId: number) => {
		const syneContract = await backendCall.synerall.getContract(contractId)

		if (!syneContract) return
		const validPrices = syneContract.Prices.filter(price => {
			const today = dayjs()
			const started = dayjs(price.ValidFrom).isBefore(today)
			const notExpired = price.ValidTo ? dayjs(price.ValidTo).isAfter(today) : true

			return started && notExpired
		})

		// If no valid prices are found, try to find the closest future price
		if (validPrices.length === 0 && syneContract.Prices.length !== 0) {
			const closestItem = syneContract.Prices.reduce((prev, curr) => {
				return dayjs(curr.ValidFrom).unix() - dayjs().unix() <
					dayjs(prev.ValidFrom).unix() - dayjs().unix()
					? curr
					: prev
			})

			if (closestItem) {
				validPrices.push(...syneContract.Prices.filter(x => x.ValidFrom === closestItem.ValidFrom))
			}
		}

		const syneSpotMarginObject = validPrices.find(price => price.ComponentType === 'SpotMargin')
		const syneFixedPriceObject = validPrices.find(price => price.ComponentType === 'Energy')
		const syneMonthlyPriceObject = validPrices.find(price => price.ComponentType === 'MonthlyFee')

		if (syneSpotMarginObject) {
			setContractType('spot')
			setSpotMargin(parseFloat(syneSpotMarginObject.UnitWithVatInEur.replace(',', '.')))
		}
		if (syneFixedPriceObject) {
			setContractType('fixed')
			setFixedPrice(parseFloat(syneFixedPriceObject.UnitWithVatInEur.replace(',', '.')))
		}
		if (syneMonthlyPriceObject)
			setMonthlyPrice(parseFloat(syneMonthlyPriceObject.UnitWithVatInEur.replace(',', '.')))

		if (validPrices[0]) setEndDate(validPrices[0].ValidTo ?? null)
	}

	const getWeeklySpotAverage = async () => {
		const today = dayjs()
		const weekAgo = today.subtract(1, 'week')

		const prices = await backendCall.getExchangePrices(
			weekAgo.format('YYYY-MM-DD'),
			today.format('YYYY-MM-DD'),
			object.id
		)

		const avgPrice = prices.reduce((acc, price) => acc + price.priceWithAlv, 0) / prices.length

		setWeeklySpotAverage(avgPrice)
	}

	const getMonthlySpotAverage = async () => {
		const today = dayjs()
		const monthAgo = today.subtract(1, 'month')
		const startOfMonth = today.startOf('month')

		const prices = await backendCall.getExchangePrices(
			monthAgo.format('YYYY-MM-DD'),
			today.add(1, 'day').format('YYYY-MM-DD'),
			object.id
		)

		const last30DaysPrices = prices.filter(price => dayjs(price.utcStartHour).isAfter(monthAgo))
		const thisMonthPrices = prices.filter(price =>
			dayjs(price.utcStartHour).isSameOrAfter(startOfMonth)
		)

		const last30DaysAvgSpotPrice =
			last30DaysPrices.reduce((acc, price) => acc + price.priceWithAlv, 0) / last30DaysPrices.length

		const thisMonthAvgSpotPrice =
			thisMonthPrices.reduce((acc, price) => acc + price.priceWithAlv, 0) / thisMonthPrices.length

		setLast30DaysSpotAverage(last30DaysAvgSpotPrice)
		setThisMonthSpotAverage(thisMonthAvgSpotPrice)
	}

	return {
		contractType,
		spotMargin,
		fixedPrice,
		monthlyPrice,

		loading,

		last30DaysSpotAverage,
		weeklySpotAverage,
		thisMonthSpotAverage,

		// Lock-ins
		isLockInContract,
		currentLockIn,
		upcomingLockIns,
		oldLockIns,

		// Helpers
		value: (contractType === 'spot' ? spotMargin : fixedPrice) ?? 0,
		isSpotMargin: contractType === 'spot',
		isUnfilled: spotMargin === undefined && fixedPrice === undefined,
		endDate,
	}
}
